import { Link } from 'react-router';
import { ProviderListRow } from './types';
import { useDebouncedValue } from '@mantine/hooks';
import { SearchInput } from '@/components/SearchInput';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { ReactElement, useMemo, useState } from 'react';
import { useProviderList } from './hooks/useProviderList';
import { IconAlertCircle, IconCalendarEvent } from '@tabler/icons-react';
import { EditPhoneNumberDrawer } from './components/EditPhoneNumberDrawer';
import { Alert, Button, Container, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table';

export function ProvidersPage(): ReactElement {
  const {
    currentUserPermissions: { isAdmin, hasAdminMembership },
  } = useCurrentUser();

  const isAuthorized = isAdmin || hasAdminMembership;

  if (!isAuthorized) {
    return (
      <Container size="xl" py="md">
        <Alert icon={<IconAlertCircle size={16} />} title="Access Denied" color="red">
          This page is only accessible to administrators.
        </Alert>
      </Container>
    );
  }

  return <AuthorizedProvidersPage />;
}

function AuthorizedProvidersPage(): ReactElement {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 400);
  const [selectedProvider, setSelectedProvider] = useState<ProviderListRow | null>(null);
  const { providers, loading, updateProvider } = useProviderList(debouncedSearchTerm);

  const columns = useMemo<MRT_ColumnDef<ProviderListRow>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'email',
        header: 'Email',
        Cell: ({ row }) => row.original.practitioner.telecom?.find((t) => t.system === 'email')?.value || '—',
      },
      {
        accessorKey: 'role',
        header: 'Role',
      },
      {
        accessorKey: 'phoneNumber',
        header: 'Phone number',
        Cell: ({ row }) => row.original.phoneNumber || '—',
      },
    ],
    []
  );

  const table = useMantineReactTable({
    columns,
    data: providers,
    state: {
      isLoading: loading,
    },
    enableRowSelection: false,
    enableSorting: true,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enablePagination: false,
    enableColumnActions: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableSelectAll: false,
    enableToolbarInternalActions: false,
    enableRowActions: true,
    positionActionsColumn: 'last',
    initialState: {
      density: 'xs',
      sorting: [{ id: 'name', desc: false }],
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: 'Actions',
      },
    },
    renderRowActions: ({ row }) => (
      <Button size="xs" radius="xl" color="blue" variant="light" onClick={() => setSelectedProvider(row.original)}>
        Edit phone
      </Button>
    ),
    mantineTableProps: {
      highlightOnHover: true,
    },
    mantinePaperProps: {
      shadow: 'sm',
      withBorder: true,
      style: { height: '100%', display: 'flex', flexDirection: 'column' },
    },
    mantineTableHeadCellProps: {
      style: {
        fontWeight: '500',
      },
    },
  });

  return (
    <Container
      size="xl"
      py="md"
      style={{
        height: 'calc(100vh - 60px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack gap="lg" style={{ flex: 1, overflow: 'hidden' }}>
        <Group justify="space-between" align="center">
          <Title order={1}>Providers</Title>
          <Group gap="sm">
            <Button
              color="blue"
              radius="xl"
              variant="outline"
              component={Link}
              to="/on-call-schedule"
              leftSection={<IconCalendarEvent size={16} />}
            >
              On-Call NP Schedule
            </Button>
            <SearchInput value={searchTerm} onChange={(value) => setSearchTerm(value.trim())} />
          </Group>
        </Group>

        {!loading && providers.length === 0 ? (
          <Paper withBorder p="xl">
            <Text c="dimmed" ta="center">
              No providers found.
            </Text>
          </Paper>
        ) : (
          <Paper style={{ flex: 1, overflow: 'hidden' }}>
            <MantineReactTable table={table} />
          </Paper>
        )}
      </Stack>

      <EditPhoneNumberDrawer
        opened={selectedProvider !== null}
        onClose={() => setSelectedProvider(null)}
        providerName={selectedProvider?.name ?? ''}
        practitioner={selectedProvider?.practitioner ?? null}
        onSaved={updateProvider}
      />
    </Container>
  );
}
